import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import PropertyCard from '../../components/FeaturedHomes/card';
import useAuth from '../../hooks/useAuth';
import { useGlobalState } from '../../contexts/GlobalStateContext';
import Swal from 'sweetalert2';
import imovel1 from '../../assets/imoveis/01.webp';
import imovel2 from '../../assets/imoveis/02.webp';
import imovel3 from '../../assets/imoveis/03.webp';

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 40px 24px;
`;

const HeroSection = styled.div`
  background: linear-gradient(180deg, rgba(0,0,0,0.45), rgba(0,0,0,0.25)), url('/tipos/praiaPortugal.png') center/cover no-repeat;
  text-align: center;
  padding: 100px 20px;
  margin-bottom: 40px;
  position: relative;

  h1 {
    font-size: 3rem;
    font-weight: 700;
    margin-bottom: 16px;
    color: #fff;
    text-shadow: 2px 2px 4px rgba(0,0,0,.3);
  }

  p {
    font-size: 1.25rem;
    color: #f3f4f6;
    text-shadow: 1px 1px 2px rgba(0,0,0,.25);
    max-width: 800px;
    margin: 0 auto;
  }

  @media (max-width: 768px) {
    padding: 60px 16px;
    h1 { font-size: 2rem; }
    p { font-size: 1rem; }
  }
`;

const Title = styled.h1`
  font-size: 32px;
  color: #222;
  margin-bottom: 8px;
  font-weight: 700;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin-bottom: 20px;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0,1fr));
  gap: 16px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export default function HighlightsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();

  // favoritos
  const [favorites, setFavorites] = useState([]);
  const { isLoggedIn, user } = useAuth();
  const globalState = useGlobalState();
  const handleOpenLogin = globalState?.handleOpenLogin || (()=>{});

  const isFavorited = (propertyId) => favorites.some(f => String(f.imovelFAVORITO) === String(propertyId));

  useEffect(() => {
    const fetchFavorites = async () => {
      if (!isLoggedIn || !user) return;
      const token = localStorage.getItem('auth_token');
      try {
        const res = await fetch(`${import.meta.env.VITE_URL_API}/favoritos/usuario/${user.codigo || user.id || user.userId}`, {
          headers: { accept: 'application/json', Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        setFavorites(Array.isArray(data) ? data : []);
      } catch { setFavorites([]); }
    }
    fetchFavorites();
  }, [isLoggedIn, user]);

  // Visualizar fotos (lightbox)
  function handleViewPhotos(property) {
    // Normaliza o array de fotos em URLs (aceita objetos {urlFOTO/pathFOTO})
    const raw = Array.isArray(property.fotos) && property.fotos.length ? property.fotos : [imovel1, imovel2, imovel3];
    const imgs = raw.map(x => (typeof x === 'string' ? x : (x?.urlFOTO || x?.pathFOTO || ''))).filter(Boolean);
    if (!imgs.length) return;
    let idx = 0;
    const renderHtml = (src, index, total) => `
      <div style="position:relative; display:flex; align-items:center; justify-content:center; background:#111; padding:0; margin:0;">
        <button id="lb-prev" style="position:absolute; left:12px; top:50%; transform:translateY(-50%); z-index:10; border:none; background:#fff; width:36px; height:36px; border-radius:50%; display:flex; align-items:center; justify-content:center; cursor:pointer;">❮</button>
        <img id="lb-img" src="${src}" style="max-width:90vw; max-height:80vh; object-fit:contain; border-radius:4px;" />
        <button id="lb-next" style="position:absolute; right:12px; top:50%; transform:translateY(-50%); z-index:10; border:none; background:#fff; width:36px; height:36px; border-radius:50%; display:flex; align-items:center; justify-content:center; cursor:pointer;">❯</button>
        <div id="lb-counter" style="position:absolute; bottom:12px; left:50%; transform:translateX(-50%); background:rgba(0,0,0,.6); color:#fff; padding:6px 10px; border-radius:12px; font-weight:600;">${index+1} / ${total}</div>
      </div>`;
    const update = (swalContent) => {
      const img = swalContent.querySelector('#lb-img');
      const counter = swalContent.querySelector('#lb-counter');
      if (img) img.src = imgs[idx];
      if (counter) counter.textContent = `${idx + 1} / ${imgs.length}`;
    };
    Swal.fire({
      html: renderHtml(imgs[idx], idx, imgs.length),
      width: '90%', padding: 0, background: '#111',
      showConfirmButton: false, showCloseButton: true, allowOutsideClick: false,
      didOpen: (el) => {
        const prev = el.querySelector('#lb-prev');
        const next = el.querySelector('#lb-next');
        const onPrev = () => { idx = (idx - 1 + imgs.length) % imgs.length; update(el); };
        const onNext = () => { idx = (idx + 1) % imgs.length; update(el); };
        prev?.addEventListener('click', onPrev);
        next?.addEventListener('click', onNext);
      }
    });
  }

  // Visualizar vídeo (embed YouTube/Vimeo ou iframe do backend)
  function handleViewVideo(property) {
    let raw = (property?.videoIMOVEL || '').trim();
    if (!raw) { try { Swal.fire({ toast:true, position:'top-end', timer:2000, showConfirmButton:false, icon:'info', title:'Imóvel sem vídeo' }); } catch (e) { console.error(e); } return; }
    const hasIframe = /<\s*iframe[\s\S]*><\s*\/\s*iframe\s*>/i.test(raw) || /<\s*iframe[^>]*>/i.test(raw);
    const toEmbed = (url) => {
      try {
        const u = new URL(url);
        const host = u.hostname.replace(/^www\./, '');
        if (host.includes('youtube.com')) {
          const v = u.searchParams.get('v');
          let id = v;
          if (!id && u.pathname.startsWith('/shorts/')) id = u.pathname.split('/shorts/')[1]?.split(/[?&#]/)[0];
          if (!id && u.pathname.startsWith('/embed/')) id = u.pathname.split('/embed/')[1]?.split(/[?&#]/)[0];
          if (!id && u.pathname.startsWith('/watch')) id = v;
          if (id) return `https://www.youtube.com/embed/${id}`;
          return url;
        }
        if (host.includes('youtu.be')) {
          const id = u.pathname.slice(1).split(/[?&#]/)[0];
          if (id) return `https://www.youtube.com/embed/${id}`;
          return url;
        }
        if (host.includes('vimeo.com')) {
          const id = u.pathname.replace(/\/(video|channels|ondemand)/, '').split('/').filter(Boolean)[0];
          if (id) return `https://player.vimeo.com/video/${id}`;
          return url;
        }
      } catch (e) { console.error(e); }
      return url;
    };
    let contentHtml = raw;
    if (!hasIframe) {
      const embedSrc = toEmbed(raw);
      contentHtml = `<div style="position:relative;padding-top:56.25%;width:100%;"><iframe src="${embedSrc}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;" allowfullscreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"></iframe></div>`;
    }
    Swal.fire({ title:'Vídeo do imóvel', html:`<div style="padding:0;position:relative;">${contentHtml}</div>`, width:'80%', padding:0, background:'#000', showConfirmButton:false, showCloseButton:true })
  }

  // Comparar
  function handleCompare(property) {
    const absolutize = (src) => {
      if (!src || typeof src !== 'string') return '';
      if (src.startsWith('http') || src.startsWith('data:')) return src;
      const cleaned = src.replace(/^\.*\/+/, '').replace(/^images\//, 'images/');
      const host = (import.meta.env.VITE_PUBLIC_ASSETS_HOST || '').replace(/\/$/, '');
      const base = host || String(import.meta.env.VITE_URL_API || '').replace(/\/api\/?$/, '');
      return `${base}/${cleaned}`;
    };
    const firstPhotoRaw = Array.isArray(property.fotos) && property.fotos.length
      ? (typeof property.fotos[0] === 'string' ? property.fotos[0] : (property.fotos[0]?.urlFOTO || property.fotos[0]?.pathFOTO || ''))
      : imovel1;
    const mapped = {
      id: property.codigoIMOVEL || property.id,
      title: property.tituloIMOVEL || 'Sem título',
      price: property.valorIMOVEL ?? '',
      location: property.enderecoIMOVEL || '',
      bedrooms: property.quartosIMOVEL ?? '',
      bathrooms: property.banheirosIMOVEL ?? '',
      area: property.metragemIMOVEL ?? '',
      garage: property.vagasgaragemIMOVEL ?? '',
      image: absolutize(firstPhotoRaw),
    };
    try {
      const prev = JSON.parse(localStorage.getItem('compare_items') || '[]');
      const exists = prev.some(p => String(p.id) === String(mapped.id));
      const next = exists ? prev : [...prev, mapped];
      localStorage.setItem('compare_items', JSON.stringify(next));
      if (next.length >= 2) navigate('/compare', { state: { items: next } });
    } catch (e) { console.error(e); }
  }

  const handleFavorite = async (property) => {
    if (!isLoggedIn) { handleOpenLogin(); return; }
    try {
      const token = localStorage.getItem('auth_token');
      const payload = {
        imovelFAVORITO: property.codigoIMOVEL || property.id,
        usuarioFAVORITO: user?.codigo || user?.id || user?.userId,
        ativoFAVORITO: 1,
      };
      const res = await fetch(`${import.meta.env.VITE_URL_API}/favoritos`, {
        method: 'POST',
        headers: { accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const upd = await fetch(`${import.meta.env.VITE_URL_API}/favoritos/usuario/${payload.usuarioFAVORITO}`, { headers: { accept: 'application/json', Authorization: `Bearer ${token}` }});
        const data = await upd.json();
        setFavorites(Array.isArray(data) ? data : []);
        try { Swal.fire({ toast:true, position:'top-end', timer:2000, showConfirmButton:false, icon:'success', title:'Adicionado aos favoritos' }); } catch (e) { console.error(e); }
      }
    } catch (e) { console.error(e); }
  }

  const handleUnfavorite = async (property) => {
    if (!isLoggedIn) { handleOpenLogin(); return; }
    try {
      const token = localStorage.getItem('auth_token');
      const fav = favorites.find(f => String(f.imovelFAVORITO) === String(property.codigoIMOVEL || property.id));
      if (!fav) return;
      await fetch(`${import.meta.env.VITE_URL_API}/favoritos/${fav.codigoFAVORITO}`, { method:'DELETE', headers: { accept:'*/*', Authorization: `Bearer ${token}` }});
      const upd = await fetch(`${import.meta.env.VITE_URL_API}/favoritos/usuario/${user.codigo || user.id || user.userId}`, { headers: { accept:'application/json', Authorization:`Bearer ${token}` }});
      const data = await upd.json();
      setFavorites(Array.isArray(data) ? data : []);
      try { Swal.fire({ toast:true, position:'top-end', timer:1800, showConfirmButton:false, icon:'info', title:'Removido dos favoritos' }); } catch (e) { console.error(e); }
    } catch (e) { console.error(e); }
  }

  useEffect(() => {
    const fetchPage = async () => {
      setLoading(true); setError('');
      try {
        const url = `${import.meta.env.VITE_URL_API}/imoveis?page=${page}&pageSize=${pageSize}&destaque=1&ativo=1`;
        const res = await fetch(url, { headers: { accept: 'application/json' }}); // público
        if (!res.ok) throw new Error('Falha ao buscar destaques');
        const json = await res.json();
        const list = Array.isArray(json?.data) ? json.data : (Array.isArray(json) ? json : []);
        const pg = json?.pagination || {};
        setItems(list);
        setTotalPages(Number(pg.totalPages || 1));
      } catch (e) {
        setError('Não foi possível carregar os destaques.');
        setItems([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [page, pageSize]);

  return (
    <>
      <HeroSection>
        <h1>Destaques</h1>
        <p>Explore todos os imóveis em destaque</p>
      </HeroSection>
    <Container>
      <TopBar>
        <div>
          <Title>Destaques</Title>
          <Subtitle>Todos os imóveis em destaque</Subtitle>
        </div>
        <div>
          <select value={pageSize} onChange={e=>{ setPageSize(parseInt(e.target.value,10)); setPage(1); }} style={{ padding:8, border:'1px solid #e5e7eb', borderRadius:8 }}>
            <option value={12}>12</option>
            <option value={24}>24</option>
            <option value={48}>48</option>
          </select>
        </div>
      </TopBar>

      {loading ? (
        <div>Carregando...</div>
      ) : error ? (
        <div style={{ color:'#b91c1c' }}>{error}</div>
      ) : items.length === 0 ? (
        <div>Nenhum destaque encontrado.</div>
      ) : (
        <Grid>
          {items.map((property) => (
            <PropertyCard
              key={property.codigoIMOVEL || property.id}
              property={property}
              isFavorited={isFavorited(property.codigoIMOVEL || property.id)}
              onFavorite={handleFavorite}
              onUnfavorite={handleUnfavorite}
              onViewPhotos={handleViewPhotos}
              onViewVideo={handleViewVideo}
              onCompare={handleCompare}
            />
          ))}
        </Grid>
      )}

      {!loading && totalPages > 1 && (
        <div style={{ display:'flex', gap:8, justifyContent:'center', marginTop:16 }}>
          <button disabled={page<=1} onClick={()=>setPage(p=>Math.max(1,p-1))} style={{ padding:'8px 12px', border:'1px solid #e5e7eb', borderRadius:8 }}>Anterior</button>
          <span style={{ alignSelf:'center' }}>Página {page} / {totalPages}</span>
          <button disabled={page>=totalPages} onClick={()=>setPage(p=>Math.min(totalPages,p+1))} style={{ padding:'8px 12px', border:'1px solid #e5e7eb', borderRadius:8 }}>Próxima</button>
        </div>
      )}
    </Container>
    </>
  );
}
